import { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Chip, Grid, Menu, MenuItem, Stack, Button, Alert } from '@mui/material';
import SearchToolbar from '../../components/SearchToolbar/SearchToolbar';
import SectionHeader from '../../components/SectionHeader';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/useIsMobile';
import { CircularProgress } from '@mui/material';
import NoTableDataImageSimp from '../../assets/image/empty-state-establecimientos-simp.png';
import NoTableDataImageBO from '../../assets/image/empty-state-establecimientos-backoffice.png';
import { useRoles } from '../../hooks/useRoles';
import { PeriodicosFilters } from './components/PeriodicosFilters';
import PeriodicosDrawerManager, {
    PERIODICOS_DRAWER_ACTION_VALUES
} from './components/PeriodicosDrawerManager';
import ExposedGrid from './components/ExposedGrid';
import { usePeriodicos } from './hooks/usePeriodicos';

const ExpuestosIndex = ({ isHome }: { isHome?: boolean }) => {
    const { t } = useTranslation('periodicos');
    const { isAdminBO } = useRoles();
    const { isMobile, isTablet } = useIsMobile();
    const [isTabLoading, setIsTabLoading] = useState(false);
    const [showGrid, setShowGrid] = useState(false);
    const {
        handleClickAction,
        selectedIds,
        employeesTableMenu,
        isFetching,
        selectedEmployee,
        tabIndex,
        open,
        setOpen,
        handleSelectEmployee,
        handleTabChange,
        handleAsignarPrestador,
        handleAsignarExamen,
        handleGenerarConvocatorias,
        handleExportarExcel,
        handleClearFilters,
        reloadGrid,
        handleSubmitSearch,
        handleChangeExtraFilters,
        extraFilters,
        filters,
        setFilters,
        showRiesgoError,
        handleSelectAllEmployed,
        fetchData,
        getUniqueIdByTabIndex,
        getColumnWidths,
        handleSearchAndClearSelection
    } = usePeriodicos();

    const { handleCloseCard, closeMenu, openCard, anchorEl, menuOpen } = employeesTableMenu;
    const translate = (key: string): string => t(key) as string;
    useEffect(() => {
        if (extraFilters?.idEstado !== undefined) {
            handleSubmitSearch();
        }
    }, [extraFilters?.idEstado]);

    const reloadCurrentTab = async () => {
        await handleSubmitSearch();
    };

    useEffect(() => {
        setIsTabLoading(true);
    }, []);

    const onTabChange = async (_: React.SyntheticEvent, newValue: number) => {
        if (newValue === tabIndex) {
            await reloadCurrentTab();
            return;
        }

        setShowGrid(false);
        setIsTabLoading(true);
        handleTabChange(_, newValue);
        await handleSubmitSearch();
    };

    useEffect(() => {
        if (!isFetching) {
            setIsTabLoading(false);
            setShowGrid(true);
        }
    }, [isFetching]);

    const renderActionButtons = () => {
        switch (tabIndex) {
            case 0:
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={
                            selectedIds.length === 0 || selectedIds.length < 2 || showRiesgoError
                        }
                        onClick={handleAsignarExamen}
                    >
                        {t('actions.assignExam')}
                    </Button>
                );
            case 1:
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={selectedIds.length === 0}
                        onClick={handleAsignarPrestador}
                    >
                        {t('actions.assignPrestador')}
                    </Button>
                );
            case 2:
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={selectedIds.length === 0}
                        onClick={handleGenerarConvocatorias}
                    >
                        {t('actions.generateCall')}
                    </Button>
                );
            case 3:
                return (
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" color="primary" onClick={handleExportarExcel}>
                            {t('actions.exportExcel')}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={selectedIds.length === 0}
                            onClick={() => {
                                closeMenu();
                                setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_SHIFT);
                            }}
                        >
                            {t('actions.assigmentTurns')}
                        </Button>
                    </Stack>
                );

            default:
                return null;
        }
    };

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <SectionHeader>
                        <SectionHeader.Title>{t('header.title')}</SectionHeader.Title>
                    </SectionHeader>
                </Grid>
                <Grid item xs={12}>
                    <SearchToolbar
                        inputSearchName=""
                        onChange={() => {}}
                        onClick={handleSearchAndClearSelection}
                        showSearchInput={false}
                        clearFilters={handleClearFilters}
                    >
                        <PeriodicosFilters
                            extraFilters={extraFilters}
                            handleChangeExtraFilters={handleChangeExtraFilters}
                            tabIndex={tabIndex}
                        />
                    </SearchToolbar>
                </Grid>
            </Grid>

            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
                spacing={3}
            >
                {openCard && (isMobile || isTablet) ? (
                    ''
                ) : (
                    <Grid item xs={openCard ? 6 : 12} lg={openCard ? 8 : 12}>
                        <Box sx={{ mb: 2 }}>
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 2 }}
                            >
                                <Grid item>
                                    <Tabs
                                        value={tabIndex}
                                        onChange={onTabChange}
                                        sx={{ height: 64, paddingBottom: '24px' }}
                                    >
                                        <Tab
                                            label={t('table.tabs.pendingExamn')}
                                            onClick={() => tabIndex === 0 && reloadCurrentTab()}
                                            sx={{
                                                fontSize: '1rem',
                                                fontWeight: 'bold',
                                                px: 4,
                                                py: 2
                                            }}
                                        />
                                        <Tab
                                            label={t('table.tabs.pendingPrestador')}
                                            onClick={() => tabIndex === 1 && reloadCurrentTab()}
                                            sx={{
                                                fontSize: '1rem',
                                                fontWeight: 'bold',
                                                px: 4,
                                                py: 2
                                            }}
                                        />
                                        <Tab
                                            label={t('table.tabs.pendingCall')}
                                            onClick={() => tabIndex === 2 && reloadCurrentTab()}
                                            sx={{
                                                fontSize: '1rem',
                                                fontWeight: 'bold',
                                                px: 4,
                                                py: 2
                                            }}
                                        />
                                        <Tab
                                            label={t('table.tabs.called')}
                                            onClick={() => tabIndex === 3 && reloadCurrentTab()}
                                            sx={{
                                                fontSize: '1rem',
                                                fontWeight: 'bold',
                                                px: 4,
                                                py: 2
                                            }}
                                        />
                                    </Tabs>
                                </Grid>
                                <Grid item>{renderActionButtons()}</Grid>
                            </Grid>

                            {isTabLoading ? (
                                <Box display="flex" justifyContent="center" mt={4}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <ExposedGrid
                                    tabIndex={tabIndex}
                                    items={fetchData}
                                    filters={filters}
                                    setFilters={setFilters}
                                    isFetching={isFetching}
                                    selectedEmployee={selectedEmployee}
                                    selectedIds={selectedIds}
                                    handleSelectEmployee={handleSelectEmployee}
                                    handleClickAction={handleClickAction}
                                    translate={translate}
                                    showRiesgoError={showRiesgoError}
                                    handleSelectAllEmployed={handleSelectAllEmployed}
                                    getUniqueIdByTabIndex={getUniqueIdByTabIndex}
                                    getColumnWidths={getColumnWidths}
                                />
                            )}
                        </Box>
                    </Grid>
                )}
            </Grid>

            <Menu id="basic-menu" anchorEl={anchorEl} open={menuOpen} onClose={closeMenu}>
                <Box>
                    {tabIndex === 0 && (
                        <MenuItem
                            onClick={() => {
                                closeMenu();
                                handleAsignarExamen();
                            }}
                        >
                            {t('table.actions.assignmentExam')}
                        </MenuItem>
                    )}

                    {tabIndex === 1 && (
                        <MenuItem
                            onClick={() => {
                                closeMenu();
                                setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_PRESTADOR);
                            }}
                        >
                            {t('table.actions.assignmentPrestador')}
                        </MenuItem>
                    )}

                    {tabIndex === 2 && (
                        <MenuItem
                            onClick={() => {
                                closeMenu();
                                setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_EXAM_AND_PRESTADOR);
                            }}
                        >
                            {t('table.actions.seeDetails')}
                        </MenuItem>
                    )}

                    {tabIndex === 3 &&
                        [2, 3, 4, 5].includes(
                            selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto
                        ) && (
                            <MenuItem
                                onClick={() => {
                                    closeMenu();
                                    setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_SHIFT);
                                }}
                            >
                                {t('table.actions.seeTurn')}
                            </MenuItem>
                        )}

                    {tabIndex === 3 &&
                        [4, 5].includes(
                            selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto
                        ) && (
                            <MenuItem
                                onClick={() => {
                                    closeMenu();
                                    setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_RESULTS);
                                }}
                            >
                                {t('table.actions.seeResults')}
                            </MenuItem>
                        )}
                </Box>
            </Menu>

            <PeriodicosDrawerManager
                open={open}
                setOpen={setOpen}
                selectedIds={selectedIds}
                reloadGrid={reloadGrid}
                selectedEmployee={selectedEmployee}
                reloadCurrentTab={reloadCurrentTab}
                tabIndex={tabIndex}
            />
        </>
    );
};

export default ExpuestosIndex;
